<template>
  <div>
    <v-dialog scrollable v-model="dialogs.delete" persistent max-width="400">
      <material-card
        v-if="employer"
        :title="
          $t('views.employers_view.delete_employer_{0}', [
            trans(employer, 'Name'),
          ])
        "
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>
        <v-card-actions>
          <v-btn text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="tryDelete">
            {{ $t("globals.delete") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="1000px">
      <material-card
        v-if="employer"
        :title="
          isNew
            ? $t('views.employers_view.create_new_employer')
            : $t('views.employers_view.edit_employer_{0}', [
                trans(employer, 'Name'),
              ])
        "
        :color="formColor"
        :icon="isNew ? 'edit' : 'add'"
      >
        <v-card-text>
          <v-dialog v-model="dialogs.cancel" persistent max-width="400">
            <v-card>
              <v-card-title class="headline">
                {{ $t("globals.cancel_wizard") }}
              </v-card-title>
              <v-card-text>
                {{
                  $t(
                    "components.inmemoryemployers.if_you_exit_the_wizard_any_changes_made_so_far"
                  )
                }}
              </v-card-text>
              <v-card-actions>
                <v-btn text @click="dialogs.cancel = false">
                  {{ $t("globals.keep_working") }}
                </v-btn>
                <v-btn color="primary" text @click="cancelWizard()">
                  {{ $t("globals.cancel_anyway") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-row>
            <v-stepper
              v-model="step"
              vertical
              class="elevation-0"
              style="width: 100%"
            >
              <v-stepper-step :complete="step > 1" step="1" :editable="!isNew">
                {{ $t("views.employers_view.employer_info") }}
                <small>
                  {{ $t("components.inmemorystores.fill_in_the_fields") }}
                </small>
              </v-stepper-step>

              <v-stepper-content step="1">
                <div row justify-center>
                  <v-form ref="addform" v-model="valid.add">
                    <v-container fluid>
                      <v-row>
                        <v-col xs="12" sm="6" md="4" lg="4">
                          <trans-textbox
                            v-model="employer"
                            :label="$t('globals.name')"
                            :rules="[$rules.required]"
                            field="Name"
                            :dialog="dialogs.edit"
                            class="required"
                          ></trans-textbox>
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="4">
                          <no-autocomplete-textfield
                            v-model="employer.Number"
                            :label="$t('globals.number')"
                            :rules="[
                              $rules.required,
                              $rules.numeric,
                              $rules.dynamic.upToLength(4),
                              $rules.notOnlyFourZeros,
                            ]"
                            class="required"
                          ></no-autocomplete-textfield>
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="3">
                          <trans-select
                            :item="employer"
                            propertyName="Status"
                            :items="options.Models.ParticipationStatus"
                            :label="$t('globals.status')"
                            :rules="[$rules.required]"
                            class="required"
                          ></trans-select>
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="3">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-container pa-0>
                                <v-row no-gutters>
                                  <v-col>
                                    <image-uploader-string
                                      @image-data="updateLogo($event)"
                                      :image="employer.LogoBase64"
                                      :label="$t('globals.logo')"
                                    ></image-uploader-string>
                                  </v-col>
                                  <v-col
                                    style="margin-top: 20px; margin-left: 3px"
                                    cols="auto"
                                  >
                                    <v-icon v-bind="attrs" v-on="on">
                                      help
                                    </v-icon>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </template>
                            <span>
                              {{
                                $t("components.inmemoryemployers.logo_tooltip")
                              }}
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </div>
                <v-btn color="primary" @click="step = 2" :disabled="!valid.add">
                  {{ $t("globals.continue") }}
                </v-btn>
              </v-stepper-content>

              <v-stepper-step :complete="step > 2" step="2" :editable="!isNew">
                {{ $t("views.employers_view.employer_settings") }}
                <small>
                  {{ $t("components.inmemorystores.fill_in_the_fields") }}
                </small>
              </v-stepper-step>

              <v-stepper-content step="2">
                <div row justify-center>
                  <v-form ref="employerSettingsForm" v-model="valid.settings">
                    <v-container fluid>
                      <v-row>
                        <v-col xs="12" sm="6" md="4" lg="4">
                          <trans-enum
                            :value.sync="employer.PaymentMethod"
                            :items="options.Enums.EmployerPaymentMethod"
                            :label="$t('headers.claims_payments.PaymentMethod')"
                          ></trans-enum>
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="4">
                          <trans-select
                            :item="employer"
                            propertyName="PaymentDueDay"
                            :items="options.Models.PaymentDueDay"
                            :label="
                              $t(
                                'components.plan_builder.employers.payment_due_date'
                              )
                            "
                          ></trans-select>
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="3" v-if="hasClasses">
                          <v-autocomplete
                            v-model="employer.Classes"
                            :items="classesArray"
                            :label="$t('components.plancreationwizard.classes')"
                            multiple
                            clear-icon="clear"
                            clearable
                          ></v-autocomplete>
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="3" v-if="hasHSA">
                          <v-select
                            v-model="employer.HasHealthSpendingAccount"
                            :items="[
                              { text: 'True', value: true },
                              { text: 'False', value: false },
                            ]"
                            :label="
                              $t(
                                'views.plangeneralparameters.has_health_spending_account'
                              )
                            "
                            clear-icon="clear"
                            clearable
                          ></v-select>
                        </v-col>
                        <v-col
                          xs="12"
                          sm="6"
                          md="4"
                          lg="3"
                          v-if="hasBenefitYears"
                        >
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-container pa-0>
                                <v-row no-gutters>
                                  <v-col>
                                    <v-autocomplete
                                      :label="
                                        $t(
                                          'components.employers.default_benefit_year'
                                        )
                                      "
                                      v-model="employer.BenefitYearStartingMonth"
                                      :rules="[$rules.requiredInteger]"
                                      class="required"
                                      :items="$helpers.months()"
                                      :item-text="
                                        (item) => $t('months.' + item.text)
                                      "
                                    ></v-autocomplete>
                                  </v-col>
                                  <v-col
                                    style="margin-top: 20px; margin-left: 3px"
                                    cols="auto"
                                  >
                                    <v-icon v-bind="attrs" v-on="on">
                                      help
                                    </v-icon>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </template>
                            <span>
                              {{
                                $t(
                                  "components.employers.default_benefit_year_tooltip"
                                )
                              }}
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </div>
                <v-btn text @click="step = 1">
                  {{ $t("globals.back") }}
                </v-btn>
                <v-btn
                  color="primary"
                  @click="step = 3"
                  :disabled="!valid.settings"
                >
                  {{ $t("globals.continue") }}
                </v-btn>
              </v-stepper-content>
              <v-stepper-step :complete="step > 3" step="3" :editable="!isNew">
                {{ $t("views.employers_view.employer_contact_details") }}
                <small>
                  {{ $t("components.inmemorystores.fill_in_the_fields") }}
                </small>
              </v-stepper-step>

              <v-stepper-content step="3">
                <div row justify-center>
                  <v-form ref="employerDetailsForm" v-model="valid.details">
                    <v-container fluid>
                      <v-row>
                        <v-col xs="12" sm="6" md="4" lg="4">
                          <no-autocomplete-textfield
                            v-model="employer.PhoneNumber"
                            :label="$t('globals.telephone')"
                            :rules="[rules.phone]"
                            v-mask="telephoneMask"
                          ></no-autocomplete-textfield>
                          <no-autocomplete-textfield
                            v-model="employer.PhoneNumber2"
                            :label="$t('headers.employers.phone2')"
                            :rules="[rules.phone]"
                            v-mask="telephoneMask"
                          ></no-autocomplete-textfield>
                          <!-- <no-autocomplete-textfield
                            v-model="employer.PhoneNumber"
                            :label="$t('globals.telephone')"
                            :rules="[$rules.phone]"
                          ></no-autocomplete-textfield> -->
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="4">
                          <no-autocomplete-textfield
                            v-model="employer.Fax"
                            :label="$t('globals.fax_number')"
                            :rules="[rules.phone]"
                            v-mask="telephoneMask"
                          ></no-autocomplete-textfield>
                          <!-- <no-autocomplete-textfield
                            v-model="employer.Fax"
                            :label="$t('globals.fax_number')"
                            :rules="[$rules.phone]"
                          ></no-autocomplete-textfield> -->
                        </v-col>
                        <v-col xs="12" sm="6" md="4" lg="4">
                          <no-autocomplete-textfield
                            v-model="employer.Email"
                            :label="$t('headers.contacts.email')"
                            :rules="[$rules.email]"
                          ></no-autocomplete-textfield>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </div>
                <v-btn text @click="step = 2">
                  {{ $t("globals.back") }}
                </v-btn>
                <v-btn
                  color="primary"
                  @click="continueToAddress()"
                  :disabled="!valid.details"
                >
                  {{ $t("globals.continue") }}
                </v-btn>
              </v-stepper-content>

              <v-stepper-step :complete="step > 4" step="4" :editable="!isNew">
                {{ $t("views.employers_view.employer_address") }}
                <small>
                  {{ $t("views.employers_view.add_address_to_this_employer") }}
                </small>
              </v-stepper-step>
              <v-stepper-content step="4">
                <v-container>
                  <!-- <address-form
                    ref="address"
                    :provinces="options.Models.Province"
                    :item="employer"
                    v-if="employer"
                  ></address-form> -->
                  <canada-post-address
                    :address="employer.Address"
                    @update-address="employer.Address = $event"
                    @valid="valid.address = $event"
                  />
                </v-container>
                <v-btn text @click="addressBack()">
                  {{ $t("globals.back") }}
                </v-btn>
                <v-btn
                  color="primary"
                  @click="step = 5"
                  :disabled="!valid.address"
                >
                  {{ $t("globals.continue") }}
                </v-btn>
              </v-stepper-content>

              <v-stepper-step :complete="step > 5" step="5" :editable="!isNew">
                {{ $t("views.employers_view.manage_stores") }}
                <small>
                  {{
                    $t(
                      "views.employers_view.add_or_edit_stores_for_this_employer"
                    )
                  }}
                </small>
              </v-stepper-step>

              <v-stepper-content step="5">
                <!-- <in-memory-stores
                  ref="employerStoresTable"
                  @changed="employerStoresChanged($event)"
                  :relatedId="employer.Id"
                  :stores="employerStores"
                  :filter="'EmployerId eq ' + employer.Id"
                  identifier="employerEditStores"
                ></in-memory-stores>-->
                <stores
                  ref="employerStoresTable"
                  @changed="storesChanged($event)"
                  :stores="employer.Stores"
                  identifier="employerEditStores"
                  :planIdentityId="planIdentityId"
                  :hasBillingImport="hasBillingImport"
                ></stores>
                <v-btn text @click="step = 4">
                  {{ $t("globals.back") }}
                </v-btn>
                <v-btn
                  :disabled="!storesValid('storeform')"
                  color="primary"
                  @click="step = 6"
                >
                  {{ $t("globals.continue") }}
                </v-btn>
              </v-stepper-content>

              <v-stepper-step :editable="!isNew" :complete="step > 6" :step="6">
                {{ $t("globals.manage_contacts") }}
                <small>
                  {{
                    $t(
                      "views.employers_view.add_or_edit_contacts_for_this_employer"
                    )
                  }}
                </small>
              </v-stepper-step>

              <v-stepper-content :step="6">
                <contacts
                  ref="employerContactsTable"
                  @contacts-changed="employer.Contacts = $event"
                  :contacts="employer.Contacts"
                  identifier="employerEditContacts"
                  :planIdentityId="planIdentityId"
                ></contacts>
                <v-btn text @click="step = 5">
                  {{ $t("globals.back") }}
                </v-btn>
              </v-stepper-content>
            </v-stepper>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="dialogs.cancel = true">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn :disabled="!allValid()" color="primary" @click="save">
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="dialogs.import" persistent max-width="1000px">
      <employers-import-wizard
        v-if="dialogs.import"
        @close="dialogs.import = false"
        @employers="importEmployers"
      />
    </v-dialog>
    <material-card
      :title="$t('globals.employers')"
      :color="$vuetify.theme.themes.light.secondary"
      icon="work"
    >
      <pbas-data-table
        :showSelect="false"
        ref="table"
        :dataProvider="provider"
        :rowActions="rowActions"
        :floatActions="floatActions"
        fileNamePrefix="employers"
        identifier="employersBuilderTable"
        @error="error = $event"
      ></pbas-data-table>
    </material-card>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import InMemoryEmployersStoresHeaders from "@/headers/InMemoryEmployersStores";
import Contacts from "@/components/PlanBuilder/Contacts";
import TransTextbox from "@/components/TransTextbox";
import { translate2 } from "@/trans2";
import Stores from "@/components/PlanBuilder/Stores";
import ImageUploaderString from "@/components/ImageUploaderString";
import NoAutocompleteTextfield from "@/components/NoAutocompleteTextfield";
import { tokens, mask } from "vue-the-mask";
import masker from "vue-the-mask/src/masker";
import { InMemoryProvider } from "pbas-vue-components";
import InMemoryEmployersHeaders from "@/headers/InMemoryEmployers";
import CanadaPostAddress from "@/components/CanadaPostAddress";
import TransSelect from "@/components/TransSelect";
import EmployersImportWizard from "@/components/PlanBuilder/EmployersImportWizard.vue";
import TransEnum from "../TransEnum.vue";

export default {
  components: {
    ErrorHandler,
    Contacts,
    TransTextbox,
    Stores,
    ImageUploaderString,
    NoAutocompleteTextfield,
    CanadaPostAddress,
    TransSelect,
    EmployersImportWizard,
    TransEnum,
  },
  props: [
    "employers",
    "planIdentityId",
    "hasBillingImport",
    "disabled",
    "classesArray",
    "hasClasses",
    "hasHSA",
    "hasBenefitYears",
  ],
  directives: { mask },
  data() {
    return {
      rules: {
        phone: (v) => {
          let val = this.unmask(v, this.telephoneMask);
          return !val || this.$rules.phone(val);
        },
      },
      telephoneMask: "(###) ### - ####",
      isNew: true,
      employer: null,
      headers: InMemoryEmployersHeaders.concat(InMemoryEmployersStoresHeaders),
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.employer = JSON.parse(JSON.stringify(item));
            this.isNew = false;
            this.step = 1;
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
        {
          name: "delete",
          icon: "delete",
          title: "globals.delete",
          handler: (item) => {
            this.employer = JSON.parse(JSON.stringify(item));
            this.dialogs.delete = true;
          },
          visible: () => !this.disabled,
        },
      ],
      floatActions: [
        {
          text: "globals.add_new",
          color: this.$vuetify.theme.themes.light.success,
          name: "add",
          icon: "add",
          handler: () => {
            this.employer = {
              Stores: [],
              Contacts: [],
              PlanIdentityId: this.planIdentityId,
              Identifier: Math.random().toString(),
            };
            this.isNew = true;
            this.step = 1;
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
        {
          text: "globals.bulk_import",
          color: this.$vuetify.theme.themes.light.primary,
          name: "import",
          icon: "add",
          handler: () => {
            this.dialogs.import = true;
          },
          visible: () => !this.disabled,
        },
      ],
      error: null,
      valid: {
        add: true,
        details: true,
        address: true,
        info: false,
        settings: false
      },
      dialogs: {
        delete: false,
        edit: false,
        cancel: false,
        import: false,
      },
      provider: undefined,
      step: 1,
    };
  },
  computed: {
    ...mapGetters("common", ["language"]),
    ...mapGetters("plansMembers", ["options"]),
    formColor() {
      return this.isNew
        ? this.$vuetify.theme.themes.light.success
        : this.$vuetify.theme.themes.light.primary;
    },
  },
  watch: {
    employers: {
      handler(v) {
        for (let e of v) {
          if (e.Classes && !Array.isArray(e.Classes)) {
            e.Classes = JSON.parse(e.Classes);
          }
        }
        this.provider.items = v;
        this.$refs.table.getDataAsync();
      },
    },
    classesArray: {
      handler(v) {
        if (v === null || v === undefined) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let employer of this.provider.items) {
              delete employer.Classes;
            }
          }
          this.$refs.table.getDataAsync();
          this.$emit("change-employers", this.provider.items);
        } else {
          if (this.provider !== null && this.provider !== undefined) {
            for (let employer of this.provider.items) {
              if (employer.Classes !== null && employer.Classes !== undefined) {
                employer.Classes = employer.Classes.filter(
                  (o) => v.indexOf(o) > -1
                );
              }
            }
            this.$refs.table.getDataAsync();
            this.$emit("change-employers", this.provider.items);
          }
        }
      },
    },
    // "employer.BenefitYearStartingMonth": {
    //   handler(v) {
    //     console.log("BenefitYearStartingMonth: ", v);
    //     this.$refs.employerSettingsForm.validate();
    //   }
    // }
  },
  mounted() {
    // this.$nextTick(() => {
    for (let e of this.employers) {
      if (e.Classes && !Array.isArray(e.Classes)) {
        e.Classes = JSON.parse(e.Classes);
      }
    }
    this.provider = new InMemoryProvider(this.employers, this.headers);
    this.$refs.table.getDataAsync();
    // });
  },
  methods: {
    importEmployers(employers) {
      this.provider.items = employers;
      this.$refs.table.getDataAsync();
      this.dialogs.import = false;
      this.$emit("change-employers", this.provider.items);
    },
    unmask(v, mask) {
      if (v) {
        return masker(v, mask, false, tokens);
      }
      return v;
    },
    storesChanged(stores) {
      this.employer.Stores = stores;
    },
    updateLogo(image) {
      this.employer.LogoBase64 = image;
      this.$forceUpdate();
    },
    trans(item, field) {
      return translate2(item, field);
    },
    close() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.dialogs.edit = false;
      this.employer = null;
    },
    save() {
      let item = JSON.parse(JSON.stringify(this.employer));
      // if (Object.keys(item.Address).length === 0) {
      //   item.Address = null;
      // }
      if (this.isNew) {
        this.add(item);
      } else {
        this.update(item);
      }
    },
    async update(item) {
      let employer;
      employer = this.provider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];
      this.provider.items[this.provider.items.indexOf(employer)] = item;
      this.$refs.table.getDataAsync();
      this.$emit("change-employers", this.provider.items);
      this.close();
    },
    async add(item) {
      item.Name = item.Translations.filter(
        (o) => o.LanguageCode === this.language.language
      )[0].Name;
      item.UniqueIdentifier = item.Name + "_" + item.Identifier;
      this.provider.items.push(item);
      this.$refs.table.getDataAsync();
      this.$emit("change-employers", this.provider.items);
      this.close();
    },
    async tryDelete() {
      let employer;
      employer = this.provider.items.filter(
        (o) => o.Identifier === this.employer.Identifier
      )[0];
      this.provider.items.splice(this.provider.items.indexOf(employer), 1);
      this.dialogs.delete = false;
      this.$refs.table.getDataAsync();
      this.employer = null;
      this.$emit("change-employers", this.provider.items);
    },
    cancelWizard() {
      this.dialogs.cancel = false;
      this.dialogs.edit = false;
      this.close();
    },
    continueToAddress() {
      if (this.$refs.address) {
        this.$refs.address.validate();
      }
      this.step = 4;
    },
    addressBack() {
      if (this.$refs.address) {
        this.$refs.address.resetValidation();
      }
      this.step = 3;
    },
    // addressValid() {
    //   if (!this.employer) return false;
    //   return (
    //     this.$refs.address &&
    //     (this.$refs.address.valid || !this.$refs.address.dirty)
    //   );
    // },
    storesValid(formRef) {
      if (!this.employer.Stores) return true;
      if (this.employer.Stores.length === 0) return true;
      let valid = true;
      for (var i = 0; i < this.employer.Stores.length; i++) {
        if (
          this.$refs[formRef + i.toString()] &&
          this.$refs[formRef + i.toString()].length > 0
        ) {
          valid = this.$refs[formRef + i.toString()][0].valid;
        }
        if (valid === false) break;
      }
      return valid;
    },
    allValid() {
      if (!this.employer) return false;
      return (
        this.valid.add &&
        this.valid.settings &&
        this.valid.details &&
        this.storesValid("storeform") &&
        this.valid.address
      );
    },
    loadItems(items) {
      this.provider.items = items;
      this.$refs.table.getDataAsync();
    },
  },
};
</script>
